import React, { useEffect, useState } from "react";
import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import type { FrontendErrorsEventsResource } from "@/features/observability/hooks/use-frontend-errors";
import type { FrontendErrorEvent, FrontendErrorGroup } from "@/features/observability/types/frontend-errors.types";

type Props = {
    group: FrontendErrorGroup;
    resource: FrontendErrorsEventsResource;
    onClose: () => void;
};

const frameText = (asset: string | null, line: number | null, column: number | null): string => {
    if (asset === null) return "";
    const position = [line, column].filter((value): value is number => value !== null).join(":");
    return position === "" ? asset : `${asset}:${position}`;
};

function EventItem({ event }: { event: FrontendErrorEvent }) {
    const [open, setOpen] = useState(false);
    const style = frontendErrorsFormat.categoryStyle(event.category);
    const frames = event.frames === null ? [] : event.frames;
    const code = event.errorCode !== null ? event.errorCode : event.errorClass;

    return (
        <li className="fe-event" data-testid="fe-event">
            <div className="fe-event__hd">
                <span className="fe-event__time">{frontendErrorsFormat.dateTimeSeconds(event.effectiveOccurredAt)}</span>
                <span className="fe-chip">
                    <span className="fe-swatch" style={{ background: style.color }} />
                    {style.label}
                </span>
                <span className="fe-muted">{code === null ? "—" : code}</span>
                <span className="fe-code-chip">{event.httpStatus === null ? "sin HTTP" : `HTTP ${event.httpStatus}`}</span>
                <span className="fe-event__platform">{event.platform === null ? "—" : event.platform}</span>
            </div>
            <pre className="fe-event__msg">{event.message === null ? "Sin mensaje" : event.message}</pre>
            <div className="fe-event__meta">
                <div>Ruta <span>{event.route === null ? "—" : event.route}</span></div>
                <div>Release <span>{event.release === null ? "—" : event.release}</span></div>
                <div>Dispositivo <span>{frontendErrorsFormat.short(event.deviceId)}</span></div>
                <div>Fingerprint <span>{frontendErrorsFormat.short(event.fingerprint)}</span></div>
            </div>
            {frames.length > 0 ? (
                <>
                    <button type="button" className="fe-link" aria-expanded={open} onClick={() => setOpen(!open)}>
                        <span className="material-symbols-outlined">{open ? "expand_more" : "chevron_right"}</span>
                        {`Stack · ${frames.length} frames`}
                    </button>
                    {open ? (
                        <ol className="fe-event__stack">
                            {frames.map((frame, index) => (
                                <li key={`${index}-${frame.functionName === null ? "" : frame.functionName}`}>
                                    <b>{frame.functionName === null ? "(anónima)" : frame.functionName}</b>{" "}
                                    <span className="fe-muted">{frameText(frame.asset, frame.line, frame.column)}</span>
                                </li>
                            ))}
                        </ol>
                    ) : null}
                </>
            ) : null}
        </li>
    );
}

const statusMessage = (resource: FrontendErrorsEventsResource): string | null => {
    if (resource.status === "loading" || resource.status === "idle") return "Cargando eventos…";
    if (resource.status === "forbidden") return "Tu usuario no tiene acceso a los errores del frontend.";
    if (resource.status === "unavailable") return "Fuente no disponible: no se pueden mostrar los eventos ahora mismo.";
    if (resource.status === "error") return "No se han podido cargar los eventos.";
    return null;
};

export function FrontendErrorsEventsDrawer({ group, resource, onClose }: Props) {
    useEffect(() => {
        const onKey = (event: KeyboardEvent): void => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    const style = frontendErrorsFormat.categoryStyle(group.category);
    const message = statusMessage(resource);
    const events = resource.events;
    const retryable = resource.status === "unavailable" || resource.status === "error";

    return (
        <div className="fe-drawer">
            <div className="fe-drawer__scrim" onClick={onClose} aria-hidden="true" />
            <aside className="fe-drawer__panel" role="dialog" aria-modal="true" aria-label="Últimos eventos">
                <div className="fe-drawer__hd">
                    <div className="fe-drawer__top">
                        <div className="fe-muted">{`Últimos eventos · fingerprint ${frontendErrorsFormat.short(group.fingerprint)}`}</div>
                        <button type="button" className="btn btn--ghost btn--icon" aria-label="Cerrar" onClick={onClose}>
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>
                    <div className="fe-drawer__title">
                        <span className="fe-chip">
                            <span className="fe-swatch" style={{ background: style.color }} />
                            {style.label}
                        </span>
                        <span className="fe-drawer__code">{group.errorCode === null ? "Sin código" : group.errorCode}</span>
                        {group.errorClass === null ? null : <span className="fe-muted">{group.errorClass}</span>}
                    </div>
                    <div className="fe-muted">
                        {`${group.route === null ? "—" : group.route} · ${frontendErrorsFormat.count(group.count)} ocurrencias · ${frontendErrorsFormat.count(group.affectedDevicesApprox)} dispositivos`}
                    </div>
                </div>
                {resource.status === "degraded" ? (
                    <div className="fe-notice fe-notice--warn fe-drawer__notice">
                        <span className="material-symbols-outlined">warning</span>
                        Datos parciales: puede haber eventos que no se muestran
                    </div>
                ) : null}
                <div className="fe-drawer__body">
                    {message !== null ? (
                        <div className="fe-drawer__state">
                            {message}
                            {retryable ? (
                                <button type="button" className="btn btn--sm" onClick={resource.refresh}>Reintentar</button>
                            ) : null}
                        </div>
                    ) : null}
                    {message === null && events !== null && events.length === 0 ? (
                        <div className="fe-drawer__state">No hay eventos de este fingerprint en el periodo.</div>
                    ) : null}
                    {message === null && events !== null && events.length > 0 ? (
                        <ul className="fe-events">
                            {events.map((event) => <EventItem key={event.receiptId} event={event} />)}
                        </ul>
                    ) : null}
                </div>
                <div className="fe-drawer__ft">
                    <span>{events === null ? "" : `Mostrando ${frontendErrorsFormat.count(events.length)} eventos`}</span>
                    {resource.error !== null && message === null ? (
                        <span className="fe-drawer__error">No se han podido cargar más eventos.</span>
                    ) : null}
                    {resource.hasMore ? (
                        <button type="button" className="btn btn--sm" disabled={resource.loadingMore} onClick={resource.loadMore}>
                            {resource.loadingMore ? "Cargando…" : "Cargar más"}
                        </button>
                    ) : null}
                </div>
            </aside>
        </div>
    );
}
